import { supabase, isSupabaseConfigured } from '@/lib/supabase';

// Registro de auditoría desnormalizado con el nombre/correo del usuario que
// ejecutó la acción (join con `app_users` por user_id = auth.uid).
export interface AuditLog {
  id: string;
  userId: string | null;
  userName: string;
  userEmail: string;
  entityType: string;
  entityId: string;
  action: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
}

export interface AuditResult {
  data: AuditLog[];
}

// Filas reales de Supabase.
interface DbAuditLog {
  id: string;
  user_id: string | null;
  entity_type: string;
  entity_id: string;
  action: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
}

interface DbUser {
  id: string;
  full_name: string | null;
  email: string | null;
}

function mapLog(row: DbAuditLog, userMap: Map<string, DbUser>): AuditLog {
  const user = row.user_id ? userMap.get(row.user_id) : undefined;
  return {
    id: row.id,
    userId: row.user_id,
    userName: user?.full_name ?? '',
    userEmail: user?.email ?? '',
    entityType: row.entity_type,
    entityId: row.entity_id,
    action: row.action,
    fieldName: row.field_name,
    oldValue: row.old_value,
    newValue: row.new_value,
    createdAt: row.created_at,
  };
}

// Consulta el historial de auditoría uniendo `audit_logs` con `app_users` para
// mostrar quién hizo qué. Sin datos devuelve un array vacío (honesto).
export async function fetchAuditLogs(limit = 200): Promise<AuditResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const [logsRes, usersRes] = await Promise.all([
      supabase.from('audit_logs').select('*').order('created_at', { ascending: false }).limit(limit),
      supabase.from('app_users').select('id, full_name, email'),
    ]);

    if (logsRes.error) throw new Error(logsRes.error.message);
    if (usersRes.error) throw new Error(usersRes.error.message);

    const userMap = new Map(
      ((usersRes.data ?? []) as DbUser[]).map((u) => [u.id, u]),
    );

    const data = ((logsRes.data ?? []) as DbAuditLog[]).map((row) =>
      mapLog(row, userMap),
    );

    return { data };
  } catch (err) {
    console.error('[audit] connection failed:', err);
    throw err;
  }
}

// Devuelve las actividades más recientes (para la campanita de notificaciones).
export async function fetchRecentActivity(limit = 3): Promise<AuditLog[]> {
  const res = await fetchAuditLogs(limit);
  return res.data.slice(0, limit);
}

export type AuditAction = 'CREATE' | 'UPDATE' | 'DELETE' | string;

export interface AuditEntryInput {
  entityType: string;
  entityId: string;
  action: AuditAction;
  fieldName?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
}

// Registra una operación en `audit_logs` de forma no bloqueante. Nunca lanza:
// si falla, solo se advierte en consola para no interrumpir la operación
// principal. El user_id se resuelve desde la sesión activa (auth.uid).
export async function recordAudit(input: AuditEntryInput): Promise<void> {
  if (!isSupabaseConfigured || !supabase) return;

  try {
    let userId: string | null = null;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      userId = sessionData.session?.user?.id ?? null;
    } catch {
      userId = null;
    }

    const { error } = await supabase.from('audit_logs').insert({
      user_id: userId,
      entity_type: input.entityType,
      entity_id: input.entityId,
      action: input.action,
      field_name: input.fieldName ?? null,
      old_value: input.oldValue ?? null,
      new_value: input.newValue ?? null,
    });

    if (error) console.warn('[audit] record failed:', error.message);
  } catch (err) {
    console.warn('[audit] record connection failed:', err);
  }
}